import React, { useRef, useEffect } from "react";
import { PrismicRichText } from "@prismicio/react";
import HeroCard from "./heroCard";
import linkArrow from "../assets/images/icn-arrow-right.svg";
import { linkResolver } from "../linkResolver";

const DarkHero = (
  {
    title,
    taglineImage,
    taglineImageSize,
    summary,
    summaryImage,
    cardHeader,
    cardSection,
    cardSectionBorder,
    cardDivider,
    cardBenefitsDescription,
    cardBenefitsHeader,
    cardBenefitsPodBorder,
    cardBenefitsPods,
    cardFooterIcon,
    cardFooterDescription,
    anchorOrLink,
    anchorId,
    cardFooterCtaText,
    cardFooterCtaLink,
    buttonBorder
  }
) => {
  const cardSectionRef = useRef(null);
  const summarySection = useRef(null);

  useEffect(() => {
    const updateSpacing = () => {
      if (cardSectionRef.current) {
        // Measure card body and footer separately
        const cardBody = cardSectionRef.current.querySelector('.heroCardBody');
        const cardFooter = cardSectionRef.current.querySelector('.heroCardFooter');

        const bodyHeight = cardBody?.offsetHeight || 0;
        const footerHeight = cardFooter?.offsetHeight || 0;
        const halfBodyHeight = bodyHeight / 2;

        // Apply padding-bottom to image section to protect overlay text
        if (summarySection.current) {
          summarySection.current.style.paddingBottom = `${halfBodyHeight + 120}px`;
        }

        // Set CSS variable for page container spacing - include full footer height
        const pageContainer = cardSectionRef.current.closest('section')?.nextElementSibling;
        if (pageContainer) {
          const isSmallScreen = window.innerWidth <= 1050;
          const extraPadding = isSmallScreen ? 50 : 0;
          const totalOffset = halfBodyHeight + footerHeight + 70 + extraPadding;
          pageContainer.style.setProperty('--hero-card-height', `${totalOffset}px`);
        }
      }
    };

    // Initial measurement with slight delay to ensure all content is rendered
    const measurementTimer = setTimeout(updateSpacing, 0);

    // Watch for dimension changes on card section and footer
    const resizeObserver = new ResizeObserver(updateSpacing);
    if (cardSectionRef.current) {
      resizeObserver.observe(cardSectionRef.current);
      // Also observe footer specifically to catch footer sizing changes
      const footer = cardSectionRef.current.querySelector('.heroCardFooter');
      if (footer) {
        resizeObserver.observe(footer);
      }
      // Observe footer body specifically for mobile layout changes
      const footerBody = cardSectionRef.current.querySelector('.heroCardFooterBody');
      if (footerBody) {
        resizeObserver.observe(footerBody);
      }
    }

    // Listen for 1050px breakpoint changes to recalculate extra padding
    const mediaQuery = window.matchMedia('(max-width: 1050px)');
    const handleBreakpointChange = () => {
      requestAnimationFrame(() => {
        setTimeout(updateSpacing, 100);
      });
    };
    mediaQuery.addEventListener('change', handleBreakpointChange);

    return () => {
      clearTimeout(measurementTimer);
      resizeObserver.disconnect();
      mediaQuery.removeEventListener('change', handleBreakpointChange);
    };
  }, [cardBenefitsPods]);

  const isValidRichText = Array.isArray(cardFooterDescription) &&
    cardFooterDescription.every(
      (block) => block && typeof block === 'object' && 'spans' in block && Array.isArray(block.spans)
    );

  // Top border color for overlaying card section
  const borderColorMap = {
    "Gray": "#cccccc",
    "Red": "#ff4338",
    "Teal": "#00bcb4"
  };

  const cardSectionBorderColor = borderColorMap[cardSectionBorder] || "#00bcb4";

  // Hero image size mapping for max-height
  const taglineImageSizeMap = {
    "Small": "200px",
    "Medium": "270px",
    "Large": "325px"
  };

  const taglineImageSizeValue = taglineImageSizeMap[taglineImageSize] || "270px";
  console.log("taglineImageSize:", taglineImageSize); // Debugging line

  // Determine link based on anchorOrLink boolean with validation
  let ctaHref = "#";
  let isAnchorLink = false;

  if (anchorOrLink && anchorId) {
    // Use anchor link if boolean is true and anchorId exists
    ctaHref = `#${anchorId}`;
    isAnchorLink = true;
  } else if (!anchorOrLink && cardFooterCtaLink) {
    // Use regular link if boolean is false and cardFooterCtaLink exists
    ctaHref = linkResolver(cardFooterCtaLink) || "#";
  }
  // Otherwise ctaHref remains "#" as fallback

  // Handle anchor link clicks
  const handleCtaClick = (e) => {
    if (isAnchorLink) {
      e.preventDefault();
      const element = document.getElementById(anchorId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (ctaHref === "#") {
      // Prevent navigation if no valid link
      e.preventDefault();
    }
  };

  return (
    <>
      <section className="darkHero">
        <div className="heroTextWrapper">
          {isValidRichText ? (
            <PrismicRichText field={title} />
          ) : (
            <p>{title?.[0]?.text ?? ""}</p>
          )}
        </div>
        <div className="heroImageWrapper">
          <img src={taglineImage?.url} alt={taglineImage?.alt} style={{ maxHeight: taglineImageSizeValue }} />
        </div>
      </section>
      <section className="darkHeroSummarySection" ref={summarySection} style={{ backgroundImage: `url(${summaryImage?.url})` }}>
        <div className="darkHeroSummary">
          {isValidRichText ? (
            <PrismicRichText field={summary} />
          ) : (
            <p>{summary?.[0]?.text ?? ""}</p>
          )}
        </div>
        <div>
          <div className="heroCardSection" ref={cardSectionRef}>
            <div className="heroCardBody" style={{ "--card-border-color": cardSectionBorderColor }}>
              <div className="smallDarkHeaderBlock">
                {cardHeader}
              </div>
              {/* HERO CARD SECTION */}
              <HeroCard
                cardSection={cardSection}
              />
              {/* HERO CARD BENEFITS SECTION */}
              {(cardBenefitsDescription || cardBenefitsHeader || (cardBenefitsPods?.length > 0)) && (
                <div className="heroCardBenefits">
                  {/* HERO CARD SECTION DIVIDER */}
                  {cardDivider && (
                    <div className="sectionDivider">
                    </div>
                  )}
                  {/* HERO CARD BENEFITS ITALIC DESCRIPTION */}
                  {cardBenefitsDescription && (
                    <div className="heroCardBenefitsDescription">
                      {cardBenefitsDescription}
                    </div>
                  )}
                  {/* HERO CARD BENEFITS HEADER */}
                  {cardBenefitsHeader && (
                    <div className="smallDarkHeaderBlock">
                      {cardBenefitsHeader}
                    </div>
                  )}
                  {/* HERO CARD BENEFITS PODS */}
                  {cardBenefitsPods?.length > 0 && (
                    <div className="heroCardBenefitsPods" style={{
                      "--card-border-color": borderColorMap[cardBenefitsPodBorder] || "#00bcb4",
                      "--pod-count": cardBenefitsPods?.length || 1
                    }}>
                      {cardBenefitsPods?.map((pod, index) => (
                        <div key={index} className="heroCardBenefitsPod" style={{
                          "--card-border-color": borderColorMap[cardBenefitsPodBorder] || "#00bcb4"
                        }}>
                          <div className="heroCardBenefitsPodHeader">
                            <h5>{pod.benefits_pod_title}</h5>
                          </div>
                          <div className="heroCardBenefitsPodDescription">
                            {pod.benefits_pod_description}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* HERO CARD FOOTER SECTION */}
            <div className="heroCardFooter">
              <div className="heroCardFooterIcon">
                <img src={cardFooterIcon?.url} alt={cardFooterIcon?.alt} />
              </div>
              <div className="heroCardFooterBody">
                <div className="heroCardFooterInfo">
                  {isValidRichText ? (
                    <PrismicRichText field={cardFooterDescription} />
                  ) : (
                    <p>{cardFooterDescription?.[0]?.text ?? ""}</p>
                  )}
                </div>
                <div className="heroCardFooterCta">
                  <a
                    className="btn-inverse"
                    href={ctaHref}
                    onClick={handleCtaClick}
                    style={{ border: buttonBorder ? "" : "none" }}
                  >
                    <span className="btn-content">
                      {cardFooterCtaText}
                      <img src={linkArrow} alt="Arrow" width="24" height="20" />
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default DarkHero;